import classNames from 'classnames';
import * as React from 'react';
import { predicateBoolean, predicateNumber } from 'utilities/isType.js';
import { AccordionItemNew } from './accordion-item-new.js';

export interface AccordionNewProps {
  headers: React.ReactNode[];
  headersAdditional?: React.ReactNode[];
  bodies: React.ReactNode[];
  className?: string;
  warningsSeverities?: Array<string>;
  dataComponent: string;
  defaultToOpen: boolean;
  openIndexAsDefault: false | number;
  restrictToOnlyOneChildOpen?: boolean;
  finalComponent?: JSX.Element[];
  onItemOpened?: (index: number) => void;
}

/**
 * Takes in a list of headers and bodies to render of the accordion tabs
 *
 * |*************|***|
 * | Header      | V |
 * |*************|***|
 * |                 |
 * |      Body       |
 * |                 |
 * |*****************|
 *
 * The headers prop matches directly to the bodies prop (i.e. their lengths should be the same).
 * By default there are no tabs are open and any number can be open at once.
 *
 */

type ActionSetHeaders = {
  type: HeaderState.ACTION_HEADERS_SET;
  headers: AccordionNewProps['headers'];
  defaultToOpen: boolean;
};

export function setActionHeaders(headers: AccordionNewProps['headers'], defaultToOpen: boolean): ActionSetHeaders {
  return {
    type: HeaderState.ACTION_HEADERS_SET,
    headers,
    defaultToOpen,
  };
}

type ActionUpdateSingleHeaders = {
  type: HeaderState.ACTION_HEADERS_UPDATE_SINGLE;
  state: { index: number; override?: boolean; onlyOne?: boolean };
};

export function setActionUpdateSingleHeader({
  index,
  override,
  onlyOne,
}: {
  index: number;
  override?: boolean;
  onlyOne?: boolean;
}): ActionUpdateSingleHeaders {
  return {
    type: HeaderState.ACTION_HEADERS_UPDATE_SINGLE,
    state: { index, override, onlyOne },
  };
}

type ActionUpdateAllHeaders = {
  type: HeaderState.ACTION_HEADERS_UPDATE_ALL;
  state: boolean;
};

export function setActionUpdateAllHeaders(state: boolean): ActionUpdateAllHeaders {
  return {
    type: HeaderState.ACTION_HEADERS_UPDATE_ALL,
    state,
  };
}

type ActionHeaders = ActionSetHeaders | ActionUpdateSingleHeaders | ActionUpdateAllHeaders;

enum HeaderState {
  ACTION_HEADERS_SET = 'ACTION_HEADERS_SET',
  ACTION_HEADERS_UPDATE_SINGLE = 'ACTION_HEADERS_UPDATE_SINGLE',
  ACTION_HEADERS_UPDATE_ALL = 'ACTION_HEADERS_UPDATE_ALL',
}

const setAllItemsToState = (state: Record<number, boolean> = {}, newState: boolean) => {
  for (const key in state) {
    state[key] = newState;
  }
  return { ...state };
};

function AccordionReducer(state: Record<number, boolean> = {}, action: ActionHeaders): Record<number, boolean> {
  switch (action.type) {
    case HeaderState.ACTION_HEADERS_SET: {
      const newHeaders = action.headers.reduce<Record<number, boolean>>((acc, _, index) => {
        acc[index] = state[index] ?? action.defaultToOpen;
        return acc;
      }, {});
      return newHeaders;
    }
    case HeaderState.ACTION_HEADERS_UPDATE_SINGLE: {
      const currentItemState = state[action.state.index];
      // if has override use that otherwise toggle
      const newItemState = predicateBoolean(action.state.override) ? action.state.override : !currentItemState;
      // closing and only allowed one item
      const partialState = action.state.onlyOne ? setAllItemsToState(state, false) : state;
      const newState = { ...partialState, ...{ [action.state.index]: newItemState } };
      return newState;
    }
    case HeaderState.ACTION_HEADERS_UPDATE_ALL: {
      const newState = { ...setAllItemsToState(state, action.state) };
      return newState;
    }
    default:
      return state;
  }
}

/** This should be wrapped in a Accordion Style Component */
export const AccordionNew = ({
  headers,
  headersAdditional = [],
  bodies,
  className,
  warningsSeverities,
  defaultToOpen,
  restrictToOnlyOneChildOpen,
  openIndexAsDefault,
  dataComponent,
  finalComponent,
  onItemOpened,
}: AccordionNewProps) => {
  const [accordianHeaderState, setAccordianHeaderState] = React.useReducer<
    React.Reducer<Record<number, boolean>, ActionHeaders>
  >(AccordionReducer, {});

  React.useEffect(() => {
    /**
     * We have to add fallback to defaultToOpen as the warnings object periodically updates
     * This causes re-rendering of the headers which can change the dropdown header state.
     * Now when we update the headers we set to the previous state state unless it doesn't exist
     * Then we fallback to defaultToOpen
     */
    setAccordianHeaderState(setActionHeaders(headers, defaultToOpen));
  }, [headers, defaultToOpen]);

  React.useEffect(() => {
    if (predicateNumber(openIndexAsDefault)) {
      setAccordianHeaderState(setActionUpdateSingleHeader({ index: openIndexAsDefault, override: true }));
    }
  }, [openIndexAsDefault]);

  const toggleCallback = (index: number) => {
    setAccordianHeaderState(setActionUpdateSingleHeader({ index, onlyOne: restrictToOnlyOneChildOpen }));
  };

  return (
    <ul className={classNames('Accordion', className)} data-component={'accordian '.concat(dataComponent)}>
      {headers.map((header, index) => (
        <AccordionItemNew
          key={index}
          body={bodies[index]}
          heading={header}
          toggleCallback={toggleCallback}
          index={index}
          isOpen={accordianHeaderState[index]}
          onOpen={onItemOpened}
          warningSeverityClass={warningsSeverities?.[index]}
          headingAdditional={headersAdditional?.[index]}
        />
      ))}
      {finalComponent?.map((component, index) => (
        <li key={index} className="Accordion-item Accordion-head-wrapper">
          {component}
        </li>
      ))}
    </ul>
  );
};
